class ClerkFeaturedProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // If we haven't initialized yet
    if (!this.initialized) {
      this.init();
    }
  }

  getConfig() {
    const config = this.querySelector('[data-config]');
    if (config) {
      return JSON.parse(config.textContent);
    }
    return {};
  }

  init() {
    this.config = this.getConfig();
    this.dataContent = this.querySelector('[data-content]');
    this.dataSource = this.querySelector('[data-clerk-source]');
    
    // Safety check: if required elements are missing, don't proceed yet
    if (!this.dataContent || !this.dataSource) return;

    this.addEventListeners();
    this.waitForClerkContent();
    this.initialized = true;
  }

  waitForClerkContent() {
    if (!this.dataSource) return;

    // Check if content is already present (e.g. if Clerk loaded very fast)
    if (this.hasContent()) {
      this.processClerkContent();
      return;
    }

    // Observe the data source for changes (Clerk injection)
    const observer = new MutationObserver((mutations) => {
      if (this.hasContent()) {
        observer.disconnect();
        this.processClerkContent();
      }
    });

    observer.observe(this.dataSource, {
      childList: true,
      subtree: true
    });
  }

  hasContent() {
    // We look for the inner clerk-slider or any product links
    return this.dataSource.querySelectorAll('a[href*="/products/"]').length > 0;
  }

  processClerkContent() {
    // Extract Headline
    const headline = this.dataSource.querySelector('.headline h2');
    if (headline) {
      const headerElement = this.querySelector('[data-header] h2');
      if (headerElement) {
        headerElement.textContent = headline.textContent.trim();
      }
    }

    const links = this.dataSource.querySelectorAll('a[href*="/products/"]');
    const handles = new Set();

    links.forEach(link => {
      try {
        // Extract handle from URL: /products/handle-name?params...
        const url = new URL(link.href, window.location.origin);
        const pathParts = url.pathname.split('/');
        // Usually /products/handle - grab the last part that isn't empty
        const handle = pathParts[pathParts.length - 1] || pathParts[pathParts.length - 2];
        if (handle) {
          handles.add(handle);
        }
      } catch (e) {
        console.error('Error parsing product link:', link.href);
      }
    });

    if (handles.size > 0) {
      this.fetchProducts(Array.from(handles));
    }
  }

  async fetchProducts(handles) {
    const productPromises = handles.map((handle) => 
      fetch(`/products/${handle}?view=clerk-item`)
        .then((res) => res.text())
        .catch((e) => {
          console.error(e);
          return '';
        })
    );

    const productCards = await Promise.all(productPromises);
    
    // Filter out empty results in case of errors
    const validCards = productCards.filter(card => card.trim() !== '');
    
    this.dataContent.innerHTML = validCards.join('');
    
    // Initialize button state after products are loaded
    this.checkButtonState();
  }

  addEventListeners() {
    const prevButton = this.querySelector('button[data-prev]');
    const nextButton = this.querySelector('button[data-next]');

    const getScrollBy = () => {
      // Target the product card specifically to avoid selecting <link> or <script> tags that have 0 width
      const item = this.dataContent.querySelector('.product-card');
      if (!item) return 0;
      
      const style = window.getComputedStyle(this.dataContent);
      const gap = parseFloat(style.gap) || 0;
      
      // Determine scroll step: configured value on desktop, fixed 2 on mobile
      const isMobile = window.innerWidth < 990;
      const scrollStep = isMobile ? 2 : (this.config.scroll_by || 4);
      
      // Use getBoundingClientRect for sub-pixel precision
      const itemWidth = item.getBoundingClientRect().width;
      return (itemWidth + gap) * scrollStep;
    };

    const scroll = (direction) => {
      const distance = getScrollBy();
      this.dataContent.scrollBy({
        left: direction === 'next' ? distance : -distance,
        behavior: 'smooth'
      });
    };

    if (prevButton) prevButton.addEventListener('click', () => scroll('prev'));
    if (nextButton) nextButton.addEventListener('click', () => scroll('next'));
    
    this.dataContent.addEventListener('scroll', () => this.checkButtonState());
    window.addEventListener('resize', () => this.checkButtonState());
  }

  checkButtonState() {
    const prevButton = this.querySelector('button[data-prev]');
    const nextButton = this.querySelector('button[data-next]');
    
    if (!prevButton || !nextButton || !this.dataContent) return;

    // Check if scroll is at the start
    const isAtStart = this.dataContent.scrollLeft <= 0;
    prevButton.disabled = isAtStart;

    // Check if scroll is at the end
    // We use a small tolerance (1px) for cross-browser consistency
    const isAtEnd = this.dataContent.scrollLeft + this.dataContent.clientWidth >= this.dataContent.scrollWidth - 1;
    nextButton.disabled = isAtEnd;
  }
}

if (!customElements.get('clerk-featured-products')) {
  customElements.define('clerk-featured-products', ClerkFeaturedProducts);
}

// Global function to initialize Clerk sliders in articles
window.initArticleClerkSliders = (settings) => {
  const articleContent = document.querySelector('.article-template__content');
  if (!articleContent) return;

  const clerkSpans = articleContent.querySelectorAll('.clerk');
  
  clerkSpans.forEach(span => {
    // Skip if already processed
    if (span.closest('clerk-featured-products')) return;

    // Create the custom element wrapper
    const wrapper = document.createElement('clerk-featured-products');
    
    // Set styles
    // Programmatically add 0.15 to per_view
    const perViewBase = parseInt(settings.per_view || 4);
    const perViewReal = perViewBase + 0.15;
    
    wrapper.style.setProperty('--clerk-per-view', perViewReal);
    wrapper.style.setProperty('--clerk-gap', '8px');

    // Create config script
    const script = document.createElement('script');
    script.type = 'application/json';
    script.setAttribute('data-config', '');
    script.textContent = JSON.stringify({
      scroll_by: settings.scroll_by || 4,
      per_view: perViewReal
    });

    // Create hidden source container and move span into it
    const sourceDiv = document.createElement('div');
    sourceDiv.hidden = true;
    sourceDiv.setAttribute('data-clerk-source', '');
    
    // Create header
    const headerDiv = document.createElement('div');
    headerDiv.setAttribute('data-header', '');
    headerDiv.innerHTML = `
      <h2></h2>
      <div class="clerk-navigation">
        <button data-prev><svg focusable="false" class="icon icon-arrow-left" viewBox="0 0 8 12" role="presentation"><path stroke="currentColor" stroke-width="2" d="M6 10L2 6l4-4" fill="none" stroke-linecap="square"></path></svg></button>
        <button data-next><svg focusable="false" class="icon icon-arrow-right" viewBox="0 0 8 12" role="presentation"><path stroke="currentColor" stroke-width="2" d="M2 2l4 4-4 4" fill="none" stroke-linecap="square"></path></svg></button>
      </div>
    `;

    // Create content container with placeholders
    const contentDiv = document.createElement('div');
    contentDiv.setAttribute('data-content', '');
    
    // Add placeholders
    // Generate enough placeholders to cover the partial view (e.g., 4.15 needs 5 placeholders)
    const placeholdersCount = Math.ceil(perViewReal);
    for (let i = 0; i < placeholdersCount; i++) {
      const placeholder = document.createElement('div');
      placeholder.innerHTML = `
        <div class="clerk-placeholder"></div>
        <div class="clerk-placeholder__subtext"></div>
        <div class="clerk-placeholder__title"></div>
        <div class="clerk-placeholder__price"></div>
      `;
      contentDiv.appendChild(placeholder);
    }

    // Assemble structure
    
    // 1. Save reference to insertion point
    const parent = span.parentNode;
    const reference = span.nextSibling;

    // 2. Move span into sourceDiv (removes it from DOM)
    sourceDiv.appendChild(span);
    
    // 3. Append all parts to wrapper
    wrapper.appendChild(script);
    wrapper.appendChild(sourceDiv);
    wrapper.appendChild(headerDiv);
    wrapper.appendChild(contentDiv);

    // 4. Insert wrapper into the original parent at the original location
    // This triggers connectedCallback, and since children are already appended, init() works.
    if (reference) {
      parent.insertBefore(wrapper, reference);
    } else {
      parent.appendChild(wrapper);
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  window.initArticleClerkSliders();
});